import React, { useState, useEffect } from 'react';
import { db } from '../../services/firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { Image as ImageIcon, FileVideo, Play, Loader2 } from 'lucide-react';

const MediaGalleryPreview = ({ gameId, className = '' }) => {
    const [mediaItems, setMediaItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!gameId) return;

        const mediaRef = collection(db, 'games', gameId, 'media');
        const q = query(mediaRef, orderBy('uploadedAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = [];
            snapshot.forEach((doc) => {
                items.push({ id: doc.id, ...doc.data() });
            });
            setMediaItems(items);
            setLoading(false);
        }, (error) => {
            console.error('Error fetching media:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [gameId]);

    if (loading) {
        return (
            <div className={`flex items-center justify-center p-4 text-gray-400 ${className}`}>
                <Loader2 className="animate-spin" size={20} />
            </div>
        );
    }

    if (mediaItems.length === 0) {
        return null; // Don't render anything if there's no media
    }

    // Display only the first 4 items as a preview, plus a count of the rest
    const previewItems = mediaItems.slice(0, 4);
    const remainingCount = Math.max(0, mediaItems.length - 4);

    return (
        <div className={`mt-4 ${className}`}>
            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                <ImageIcon size={14} />
                Match Media ({mediaItems.length})
            </h5>

            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                {previewItems.map((item, idx) => {
                    const isVideo = item.fileType?.startsWith('video/');

                    return (
                        <div
                            key={item.id}
                            className="relative aspect-square rounded-lg bg-gray-100 overflow-hidden flex flex-col items-center justify-center border border-gray-200 group"
                            title={`${item.fileName} / Uploaded by ${item.uploaderName}`}
                        >
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity z-10" />

                            {isVideo ? (
                                <>
                                    <div className="bg-indigo-100 p-2 rounded-full absolute z-20">
                                        <Play size={16} className="text-indigo-600 ml-0.5" />
                                    </div>
                                    <FileVideo size={24} className="text-gray-300 opacity-50" />
                                </>
                            ) : (
                                <ImageIcon size={24} className="text-gray-300" />
                            )}

                            {/* Uploader Name Overlay */}
                            <div className="absolute bottom-0 left-0 right-0 p-1 bg-black/50 text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity z-20 truncate text-center">
                                {item.uploaderName?.split(' ')[0]}
                            </div>
                        </div>
                    );
                })}

                {remainingCount > 0 && (
                    <div className="relative aspect-square rounded-lg bg-gray-50 flex items-center justify-center border border-dashed border-gray-300">
                        <span className="text-sm font-semibold text-gray-500">+{remainingCount}</span>
                    </div>
                )}
            </div>

            <div className="text-xs text-gray-400 mt-2 bg-gray-50 p-2 rounded inline-block">
                Media files are securely stored in the club's Google Drive.
            </div>
        </div>
    );
};

export default MediaGalleryPreview;
